import React, {MutableRefObject, useEffect, useRef, useState} from 'react';
import './dashboard.css';
import moment from 'moment'
import 'moment/locale/fr';
import 'moment/locale/nl-be';
import {Box, Grid, Typography, useTheme} from "@mui/material";
import Footer from "../../components/footer";
import useData from '../../hooks/useData';
import {Card} from '../../components/card';
import {isMobile} from 'react-device-detect';
import {useTranslation} from 'react-i18next';
import Navigation from './navigation/navigation';
import {CustomTheme} from "../../config/theme/theme";
import Technical from './technical/technical';
import {SiteResponse, UserResponse} from "../../services";
import HamburgerSvg from '../../assets/images/hamburgerSvg';
import Financial from './financial/financial';
import Advanced from "./advanced/advanced";
import ElionSwitchSvg from "../../assets/images/elionSwitch.svg";
import useAuth from "../../hooks/useAuth";

const NEW_SESSION_KEY: string = 'new_session';

export default function Dashboard() {

    const {
        api,
        smartBoxes,
        setSmartBoxes,
        setActiveSmartBox,
        isAdvanced,
        setIsAdvanced,
    } = useData();
    const {siteApi, userApi, analyticsApi} = api || {};

    const [selectedTab, setSelectedTab] = React.useState('technical')
    const [showNavigation, setShowNavigation] = React.useState<boolean>(false);
    const [user, setUser] = React.useState<UserResponse>()
    const [loadingSites, setLoadingSites] = useState<boolean>(false);
    const [incomingSmartboxes, setIncomingSmartboxes] = useState<SiteResponse[]>([]);
    const pageRef = React.useRef(null);
    const isStoringAnalytics: MutableRefObject<boolean> = useRef<boolean>(false);


    const {t} = useTranslation();
    let theme: CustomTheme = useTheme()


    function setActivePage(event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLButtonElement>, page: string) {
        event.preventDefault();
        setSelectedTab(page);
    }

    function setNavigation(value: boolean) {
        setShowNavigation(value);
    }

    useEffect(() => {
        if (api.hasToken()) {
            fetchUser();
            fetchSmartboxes();
            fetchIncomingSmartboxes();
        }
    }, [api]);

    function fetchUser() {
        const getUser = async () => {
            const response = await userApi.getApiUsersMeGet()
            setUser(response.data);
            setIsAdvanced(response.data.advanced);
            // storeAnalytics();
        };

        getUser().catch(() => {
        });
    }

    // function storeAnalytics() {
    //     const isNewSession: boolean = !sessionStorage.getItem(NEW_SESSION_KEY);
    //     if (isNewSession && !isStoringAnalytics.current) {
    //         isStoringAnalytics.current = true;
    //         analyticsApi.logAnalyticsApiAnalyticsPost().then((response) => {
    //             if (response?.status === 204) sessionStorage.setItem(NEW_SESSION_KEY, 'false');
    //         }).finally(() => isStoringAnalytics.current = false);
    //     }
    // }

    function fetchSmartboxes() {
        setLoadingSites(true);
        siteApi.getApiSitesGet().then(response => {
            let data: SiteResponse[] = response.data;
            setSmartBoxes(data);
            if (response.data.length > 0) {
                setActiveSmartBox(response.data[0]);
            }
        }).catch(() => {
        }).finally(() => {
            setLoadingSites(false);
        })
    }

    function fetchIncomingSmartboxes() {
        siteApi.getApiSitesIncomingGet().then(response => {
            let data: SiteResponse[] = response.data;
            setIncomingSmartboxes(data);
        }).catch(() => {
        });
    }

    function getDate() {
        // return moment().locale(i18next.language).format("ddd DD MMMM yyyy")
        return moment().locale('nl-BE').format("ddd DD MMMM yyyy")
    }

    return <>
        <Grid container columns={5} flexGrow={1} width="100%" maxWidth="1920px">
            {(!isMobile || showNavigation) && <Grid xs={5} lg={1} bgcolor={theme.palette.background.navbar}
                                                    sx={{display: {lg: 'block', xs: showNavigation ? 'block' : 'none'}}}
                                                    item>
                <Navigation activePage={selectedTab} setActivePage={setActivePage} showNavigation={showNavigation}
                            setShowNavigation={setNavigation} pageRef={pageRef}></Navigation>
            </Grid>}
            {(!isMobile || !showNavigation) &&
                <Grid xs={5} lg={4} sx={{
                    display: {
                        lg: 'flex',
                        xs: showNavigation ? 'none' : 'flex',
                        height: '100vh',
                        flexDirection: 'column',
                        overflowY: 'scroll'
                    }
                }}
                      bgcolor={theme.palette.background.dashboard} item>
                    <Grid flexGrow={0} container columns={12} pl={isMobile ? 1 : 2} pr={isMobile ? 1 : 2}
                          pt={isMobile ? 1 : 2}>
                        <Card xs={12} sx={{
                            background: 'white',
                            paddingY: 2.75
                        }} outerSx={{mb: 1}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" flexDirection="column">
                                    <Typography fontSize="1.1em" fontWeight="600"
                                                color={(theme: any) => theme.palette.text.primary}>
                                        {t('dashboard.header.welcome')}, {user?.firstName}
                                    </Typography>
                                    <Typography fontSize="1em" fontWeight="400"
                                                color={(theme: any) => theme.palette.text.primary}
                                                sx={{opacity: 0.7}}>
                                        Elion Dashboard
                                    </Typography>
                                </Box>
                                <Box sx={{width: '30px', '&:hover': {cursor: 'pointer'}, display: {lg: 'none', xs: 'flex'}, flexShrink: 0}}
                                     onClick={() => setShowNavigation(!showNavigation)}>
                                    <HamburgerSvg fill={theme.palette.text.primary}></HamburgerSvg>
                                </Box>
                            </Box>
                        </Card>
                        {smartBoxes.length === 0 && incomingSmartboxes.length > 0 && <Card xs={12}><Typography>
                            <b>{incomingSmartboxes.length}</b> {t('dashboard.installationIncoming')}
                            {(Array.isArray(incomingSmartboxes) ? incomingSmartboxes : []).map((sb: SiteResponse, index: number) => <Box key={sb.serialId} sx={{
                                marginBottom: 0.5,
                                marginTop: 0.5,
                                padding: 0.5,
                                display: 'flex',
                                borderRadius: '10px',
                                backgroundColor: index % 2 !== 1 ? theme.palette.background.dashboard : 'transparent'
                            }}>
                                <Box display="flex" alignItems="center" minWidth="15px"
                                     maxWidth="25px" width="4%">
                                    <img width="100%" src={ElionSwitchSvg}/></Box>
                                <Typography sx={{padding: 1}}>{sb.account} - {sb.serialId}</Typography>
                            </Box>)}
                        </Typography></Card>}
                        {!loadingSites && smartBoxes.length === 0 && incomingSmartboxes.length === 0 &&
                            <Card xs={12}><Typography fontSize={15}>
                                {t('dashboard.noInstallationsAvailable')}
                            </Typography></Card>}
                        {smartBoxes.length > 0 && selectedTab == 'technical' && <Technical/>}
                        {smartBoxes.length > 0 && selectedTab == 'financial' && <Financial/>}
                        {smartBoxes.length > 0 && selectedTab == 'advanced' && isAdvanced && <Advanced/>}
                    </Grid>
                    <Footer/>
                </Grid>}
        </Grid>
    </>
}